import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  EarlierGuests,
  GuestInvolvedAsAssessor,
  GuestInvolvedAsSharedAssessmentReader,
  GuestOnCaseDetails,
  GuestsOnCase,
  InvolveGuestAsAssessorRequest,
  InvolveGuestAsSharedAssessmentReaderRequest,
  MatchEarlierGuestsRequest,
  Person,
  PersonId,
  PersonInvolvementId,
} from "@thalamos/haven-common";
import {
  EarlierGuestsSchema,
  GuestInvolvedAsAssessorSchema,
  GuestInvolvedAsSharedAssessmentReaderSchema,
  GuestOnCaseDetailsSchema,
  GuestsOnCaseSchema,
  notificationChannels,
  personChannels,
} from "@thalamos/haven-common";

import { useLiveApiAxios, type ErrorBody } from "../axios/api.js";

const parseResponseGuestsOnCase = parseAxiosResponse(GuestsOnCaseSchema);

export const config = {
  personGuest: {
    /** The earlier guests an address reached on persons the caller reaches now. */
    matchEarlierGuests: (
      personId: PersonId,
      body: MatchEarlierGuestsRequest,
    ) => ({
      url: `/api/persons/${personId}/guests/match`,
      method: "post",
      data: body,
    }),
    /** Asks somebody outside every organisation to complete an assessment. */
    involveGuestAsAssessor: (
      personId: PersonId,
      body: InvolveGuestAsAssessorRequest,
    ) => ({
      url: `/api/persons/${personId}/guests/assessors`,
      method: "post",
      data: body,
    }),
    /** Shares finished assessments with somebody outside every organisation. */
    involveGuestAsSharedAssessmentReader: (
      personId: PersonId,
      body: InvolveGuestAsSharedAssessmentReaderRequest,
    ) => ({
      url: `/api/persons/${personId}/guests/shared-assessment-readers`,
      method: "post",
      data: body,
    }),
    /** Every guest given a link to a person, ended ones included. */
    listOnCase: (personId: string) => ({
      url: `/api/persons/${personId}/guests`,
      method: "get",
      transformResponse: parseResponseGuestsOnCase,
    }),
    /** One guest's Details, for those who may open them. */
    readDetails: (personId: PersonId, involvementId: PersonInvolvementId) => ({
      url: `/api/persons/${personId}/guests/${involvementId}`,
      method: "get",
    }),
    /** Ends a guest's access to a person at once. */
    endGuestAccess: (
      personId: PersonId,
      involvementId: PersonInvolvementId,
    ) => ({
      url: `/api/persons/${personId}/guests/${involvementId}/end`,
      method: "post",
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => ({
  personGuest: {
    /** The earlier guests offered, or undefined where the lookup was refused. */
    matchEarlierGuests: async (
      personId: PersonId,
      body: MatchEarlierGuestsRequest,
    ): Promise<EarlierGuests | undefined> => {
      const response = await apiAxios.request<unknown>(
        config.personGuest.matchEarlierGuests(personId, body),
      );

      return response.status === 200
        ? EarlierGuestsSchema.parse(response.data)
        : undefined;
    },
    /** 201 once asked; the error body otherwise, whose `message` words a refused address. */
    involveGuestAsAssessor: async (
      personId: PersonId,
      body: InvolveGuestAsAssessorRequest,
    ): Promise<
      | { status: number; data: GuestInvolvedAsAssessor; error?: undefined }
      | { status: number; data?: undefined; error: ErrorBody }
    > => {
      const response = await apiAxios.request<unknown>(
        config.personGuest.involveGuestAsAssessor(personId, body),
      );

      return response.status === 201
        ? {
            status: response.status,
            data: GuestInvolvedAsAssessorSchema.parse(response.data),
          }
        : { status: response.status, error: response.data as ErrorBody };
    },
    /** 201 once shared; the error body otherwise, whose `message` words a refused address. */
    involveGuestAsSharedAssessmentReader: async (
      personId: PersonId,
      body: InvolveGuestAsSharedAssessmentReaderRequest,
    ): Promise<
      | {
          status: number;
          data: GuestInvolvedAsSharedAssessmentReader;
          error?: undefined;
        }
      | { status: number; data?: undefined; error: ErrorBody }
    > => {
      const response = await apiAxios.request<unknown>(
        config.personGuest.involveGuestAsSharedAssessmentReader(personId, body),
      );

      return response.status === 201
        ? {
            status: response.status,
            data: GuestInvolvedAsSharedAssessmentReaderSchema.parse(
              response.data,
            ),
          }
        : { status: response.status, error: response.data as ErrorBody };
    },
    /** The guest's Details, or undefined where they could not be read. */
    readDetails: async (
      personId: PersonId,
      involvementId: PersonInvolvementId,
    ): Promise<GuestOnCaseDetails | undefined> => {
      const response = await apiAxios.request<unknown>(
        config.personGuest.readDetails(personId, involvementId),
      );

      return response.status === 200
        ? GuestOnCaseDetailsSchema.parse(response.data)
        : undefined;
    },
    /** 204 once ended; any other status where it was not. */
    endGuestAccess: async (
      personId: PersonId,
      involvementId: PersonInvolvementId,
    ): Promise<{ status: number }> => {
      const response = await apiAxios.request<unknown>(
        config.personGuest.endGuestAccess(personId, involvementId),
      );

      return { status: response.status };
    },
  },
});

export const apiHooks = () => ({
  personGuest: {
    /**
     * Every guest given a link to a person, live on their whole merge
     * tree for the reason the assessments' `forPerson` gives. `manual` holds
     * the read back until it is wanted.
     */
    onCase: function useGuestsOnCase(
      personId: string,
      person: Person | undefined,
      options?: { manual?: boolean },
    ) {
      return useLiveApiAxios<GuestsOnCase>(
        [
          notificationChannels.person(personId),
          ...(person === undefined ? [] : personChannels(person)),
        ],
        config.personGuest.listOnCase(personId),
        options,
      );
    },
  },
});
